import * as React from 'react';
import {
  Divider, FormControl,
  FormControlLabel, FormGroup,
  Grid, Radio, RadioGroup, Switch,
  Typography
} from "@mui/material";
import {
  getMaxPrefermentPctForType,
  getMaxSliderPrefermentPctForType,
  getPrefermentPctTypeOptionLabel, ingredientTypeGrain, ingredientTypeInclusion, ingredientTypeLiquid,
  ingredientTypeTopping,
  LEVAIN_PCT_TYPE_PFF,
  LEVAIN_PCT_TYPE_TOTAL_WT, SUBMIX_TYPE_SCALD
} from "../shared/Types";
import {
  getYeastPercentFromId,
  getYeastRatioSelectLabel,
  getMaxYeastRatioId
} from "../shared/Yeast";
import {toFixedDecimal} from "../utils/NumberUtils";
import {useState} from "react";
import {
  helpFermVolTemp
} from "../help/PopupHelpContent";
import {ContextualHelpDialog} from "../help/ContextualHelpDialog";
import {
  defaultBulkFermPct,
  doughVolumes,
  fermTemps,
  getLevainTime,
  getYeastTime
} from "../shared/FermentationTiming";
import {FermentationTimingControl} from "../shared/FermentationTimingControl";
import {IngredientGroupForm} from "../shared/IngredientGroupForm";
import {IngredientCollectionForm} from "../shared/IngredientCollectionForm";
import {
  mainFormDataDefaults,
  mainLevainMax,
  mainLevainMin,
  mainLevainSliderMax,
  mainLevainSliderMin, mainPrefMin,
  mainYeastMin, mainYeastSliderMin
} from "./DoughFormDefaults";
import * as Types from "../shared/Types";
import {getSumForField} from "../utils/ArrayUtils";
import {FormulaSliderEditor} from "../shared/FormulaSliderEditor";
import {levainPffToTwEstimate} from "./CalculatorUtils";

function MainDoughForm(props) {
  const {formData, setFormData, prefermentLevainFermPct, prefermentHydrationPct, prefermentStarterPct,
    updateIngredientList, componentId} = props
  const [openContextualHelpDialog, setOpenContextualHelpDialog] = useState(false)
  const [extraLeavening, setExtraLeavening] = useState(hasExtraMainLeavening())
  const [contextualHelpContent, setContextualHelpContent] = useState({title: undefined, content: undefined})
  const [bulkTimingTempF, setBulkTimingTempF] = useState('75')
  const [bulkTimingPct, setBulkTimingPct] = useState(defaultBulkFermPct)


  function openHelp(title, content) {
    setContextualHelpContent({title: title, content: content})
    setOpenContextualHelpDialog(true)
  }

  function onCloseHelp() {
    setOpenContextualHelpDialog(false)
    setContextualHelpContent({title: undefined, content: undefined})
  }

  function updateFlourData(updatedFlourData) {
    setFormData({
      ...formData,
      flour: updatedFlourData
    })
  }

  function updateLiquidData(updatedLiquidData) {
    setFormData({
      ...formData,
      liquid: updatedLiquidData
    })
  }

  function updateMixinData(mixinData) {
    setFormData({
      ...formData,
      mixin: mixinData
    })
  }

  function updateToppingsData(toppingsData) {
    setFormData({
      ...formData,
      toppings: toppingsData
    })
  }

  function onPrefermentPctTypeChange(evt) {
    const maxPct = getMaxSliderPrefermentPctForType(evt.target.value)
    let prefermentPct = formData.prefermentPffPct
    if (prefermentPct > maxPct) {
      prefermentPct = maxPct
    }
    const isPffBakersPct = (evt.target.value === LEVAIN_PCT_TYPE_PFF)
    setFormData({
      ...formData,
      [evt.target.name]: evt.target.value,
      prefermentPffPct: prefermentPct,
      pffBakersPct: isPffBakersPct
    })
  }

  function onYeastRatioChange(evt) {
    let ratioId = parseInt(evt.target.value)
    if (isNaN(ratioId)) {
      ratioId = 0
    }
    setFormData({
      ...formData,
      yeastRatioId: ratioId,
      yeastPct: getYeastPercentFromId(ratioId)
    })
  }

  function onBulkTimingParamChange(evt) {
    const fieldName = evt.target.name
    if (fieldName === 'bulkTimingTempF') {
      setBulkTimingTempF(evt.target.value)
    } else if (fieldName === 'bulkTimingPct') {
      setBulkTimingPct(evt.target.value)
    }
  }

  function getYeastSliderHeading() {
    if (formData.yeastRatioId > 0) {
      return `${toFixedDecimal(getYeastPercentFromId(formData.yeastRatioId), 2)}%`
    }
    return '(None)'
  }

  function hasNoMainLeavening() {
    return !formData.includePreferment && formData.starterPct === 0 && formData.yeastRatioId === 0
  }

  function hasExtraMainLeavening() {
    return formData.includePreferment && (formData.starterPct !== 0 || formData.yeastRatioId !== 0)
  }

  function renderMainLeavening() {
    return (
            <div>
              <Divider textAlign={'right'} sx={{marginTop: -1, width: '90%'}}>
                <Typography sx={{fontSize: 12, fontWeight: 350}}>Leavening</Typography>
              </Divider>
              <FormulaSliderEditor displayLabel={'Levain %'} fieldName={'starterPct'}
                                   fieldValue={formData.starterPct} fieldContainer={formData} updateFn={setFormData}
                                   sliderColor={hasNoMainLeavening() ? "attention" : "primary"}
                                   minVal={mainLevainMin} maxVal={mainLevainMax}
                                   sliderMinVal={mainLevainSliderMin} sliderMaxVal={mainLevainSliderMax}
                                   sliderStep={1}/>
              <FormulaSliderEditor displayLabel={`Dry Yeast ${getYeastSliderHeading()}`} fieldName={'yeastRatioId'}
                                   fieldValue={formData.yeastRatioId} fieldContainer={formData}
                                   onChangeFn={onYeastRatioChange} updateFn={setFormData}
                                   sliderColor={hasNoMainLeavening() ? "attention" : "primary"}
                                   valueDisplayFn={getYeastRatioSelectLabel}
                                   minVal={mainYeastMin} maxVal={getMaxYeastRatioId()}
                                   sliderMinVal={mainYeastSliderMin} sliderMaxVal={getMaxYeastRatioId()} sliderStep={1}
                                   disableNumericEdit={true}/>
            </div>
    )
  }

  function renderExtraMainLeavening() {
    return (
      <Grid item sx={{marginLeft: 2, marginRight: 2}}>
        <FormulaSliderEditor displayLabel={'Extra Starter %'} fieldName={'starterPct'}
                             fieldValue={formData.starterPct} fieldContainer={formData} updateFn={setFormData}
                             sliderColor={hasNoMainLeavening() ? "attention" : "primary"}
                             minVal={mainLevainMin} maxVal={mainLevainMax}
                             sliderMinVal={mainLevainSliderMin} sliderMaxVal={mainLevainSliderMax} sliderStep={1}/>
        <FormulaSliderEditor displayLabel={`Extra Dry Yeast ${getYeastSliderHeading()}`} fieldName={'yeastRatioId'}
                             fieldValue={formData.yeastRatioId} fieldContainer={formData} onChangeFn={onYeastRatioChange} updateFn={setFormData}
                             sliderColor={hasNoMainLeavening() ? "attention" : "primary"}
                             valueDisplayFn={getYeastRatioSelectLabel}
                             minVal={mainYeastMin} maxVal={getMaxYeastRatioId()}
                             sliderMinVal={mainYeastSliderMin} sliderMaxVal={getMaxYeastRatioId()} sliderStep={1}
                             disableNumericEdit={true}/>
      </Grid>
    )
  }

  function onExtraLeaveningChange(evt) {
    setExtraLeavening(evt.target.checked)
    if (!evt.target.checked) {
      setFormData({
        ...formData,
        yeastRatioId: 0,
        yeastPct: 0,
        starterPct: 0})
    }
  }

  function renderPrefermentRatio() {
    return (
      <div>
        <Divider textAlign={'right'} sx={{marginTop: -1, width: '90%'}}>
          <Typography sx={{fontSize: 12, fontWeight: 350}}>Preferment</Typography>
        </Divider>
        <Grid item container direction={'row'}>
          <FormControl sx={{marginTop: '-0.7rem'}}>
            <RadioGroup aria-labelledby="prefermentPctType" row
                        defaultValue={mainFormDataDefaults.prefermentPffPctType}
                        value={formData.prefermentPffPctType}
                        name="prefermentPffPctType" onChange={onPrefermentPctTypeChange}>
              <FormControlLabel sx={{marginTop: 0}} value={Types.LEVAIN_PCT_TYPE_TOTAL_WT}
                                control={<Radio size={'small'} color={'primary'}/>}
                                label={<Typography sx={{fontSize: 12}}>{getPrefermentPctTypeOptionLabel(Types.LEVAIN_PCT_TYPE_TOTAL_WT)}</Typography>}/>
              <FormControlLabel sx={{marginTop: 0}} value={Types.LEVAIN_PCT_TYPE_PFF}
                                control={<Radio size={'small'} color={'primary'}/>}
                                label={<Typography sx={{fontSize: 12}}>{getPrefermentPctTypeOptionLabel(Types.LEVAIN_PCT_TYPE_PFF)}</Typography>}/>
            </RadioGroup>
          </FormControl>
        </Grid>
        <FormulaSliderEditor displayLabel={`Preferment ${getPrefermentPctTypeOptionLabel(formData.prefermentPffPctType)}`} fieldName={'prefermentPffPct'}
                             fieldValue={formData.prefermentPffPct} fieldContainer={formData}
                             updateFn={setFormData} sliderColor={formData.prefermentPffPct > 0 ? "primary" : "attention"}
                             minVal={mainPrefMin} maxVal={getMaxPrefermentPctForType(formData.prefermentPffPctType)}
                             sliderMinVal={mainPrefMin} sliderMaxVal={getMaxSliderPrefermentPctForType(formData.prefermentPffPctType)} sliderStep={1}/>
        <FormGroup row sx={{marginBottom: 1}}>
          <FormControlLabel control={<Switch color={'gray'} size={'small'} name={'extraLeavening'} checked={extraLeavening}
                                             onChange={onExtraLeaveningChange}/>}
                            label={<Typography fontSize={12}>Extra Leavening</Typography>}/>
        </FormGroup>
      </div>
    )
  }

  function prefermentMainDoughTimeFormatted() {
    if (formData.prefermentPffPct === 0) {
      return 'n/a'
    }
    let levainPct = formData.prefermentPffPct
    if (formData.prefermentPffPctType === LEVAIN_PCT_TYPE_PFF) {
      levainPct = levainPffToTwEstimate(formData.prefermentPffPct, prefermentHydrationPct, prefermentStarterPct)
    }
    let qualifier = ''
    if (prefermentLevainFermPct < 100) {
      qualifier = '*'
    }
    // console.log(`calc, levainPct=${levainPct}`)
    return `~${getLevainTime(bulkTimingTempF, bulkTimingPct, levainPct, formData.hydrationPct)} ${qualifier}`
  }

  function prefermentMainDoughWarningMsg() {
    if (prefermentLevainFermPct < 100) {
      return `*Time adjusted due to preferment less than 100%`
    }
    return undefined
  }

  function mainDoughTimeFormatted() {
    if (formData.starterPct === 0 && formData.yeastRatioId === 0) {
      return 'n/a'
    }
    if (formData.starterPct > 0) {
      let levainPct = formData.starterPct
      return `~${getLevainTime(bulkTimingTempF, bulkTimingPct, levainPct, formData.hydrationPct )}`
    } else {
      return `~${getYeastTime(bulkTimingTempF, bulkTimingPct, formData.yeastRatioId, formData.hydrationPct)}`
    }
  }

  function getPrefermentFlourRatio() {
    const pffPct = formData.prefermentPffPct
    const pffHydroF = prefermentHydrationPct / 100
    const pffPctType = formData.prefermentPffPctType

    if (!formData.pffBakersPct) {
      return undefined
    }
    if (pffPct > 0 && pffPctType === LEVAIN_PCT_TYPE_PFF) {
      return {name: 'PreFerm PFF', percent: formData.prefermentPffPct, percentStr: `${formData.prefermentPffPct}`, ro: true, id: 'pf'}
    } else if (pffPct > 0 && pffPctType === LEVAIN_PCT_TYPE_TOTAL_WT) {
      let pct = toFixedDecimal(pffPct / (1 + pffHydroF))
      return {name: 'Preferm Flour', percent: pct, percentStr: `${pct}`, ro: true, id: 'pf'}
    } else if (formData.starterPct > 0) {
      let pct = toFixedDecimal(formData.starterPct / (1 + (formData.starterHydrationPct / 100)))
      return {name: 'Levain Flour', percent: pct, percentStr: `${pct}`, ro: true, id: 'pf'}
    }
    return undefined
  }

  function getPrefermentLiquidRatio() {
    const pffPct = formData.prefermentPffPct
    const pffHydroF = prefermentHydrationPct / 100
    const pffPctType = formData.prefermentPffPctType

    if (!formData.pffBakersPct) {
      return undefined
    }

    if (pffPct > 0 && pffPctType === LEVAIN_PCT_TYPE_PFF) {
      const pct = toFixedDecimal( pffPct * pffHydroF)
      return {name: 'PreFerm Liquid', percent: pct, percentStr: `${pct}`, ro: true, id: 'pf'}
    } else if (pffPct > 0 && pffPctType === LEVAIN_PCT_TYPE_TOTAL_WT) {
      let pct = toFixedDecimal(pffPct - (pffPct / (1 + pffHydroF)))
      return {name: 'Preferm Liquid', percent: pct, percentStr: `${pct}`, ro: true, id: 'pf'}
    } else if (formData.starterPct > 0) {
      let pct = toFixedDecimal(formData.starterPct - (formData.starterPct / (1 + (formData.starterHydrationPct / 100))))
      return {name: 'Levain Liquid', percent: pct, percentStr: `${pct}`, ro: true, id: 'pf'}
    }
    return undefined
  }

  function getScaldRatios() {
    const submixFlourRatios = []
    const submixLiquidRatios = []
    const scaldSubmixes = formData.submixes?.filter((sm) => sm.type === SUBMIX_TYPE_SCALD)
    if (!formData.pffBakersPct || !scaldSubmixes || scaldSubmixes.length === 0) {
      return {flourRatios: submixFlourRatios, liquidRatios: submixLiquidRatios}
    }

    // calculate the submix recipe flour % in terms of the flour share of the submix weight and the submix:main %
    scaldSubmixes.forEach((sm) => {
      let liquidPctSum = getSumForField(sm.liquid, 'percent', (v) => v / 100)
      // flour percent distribution for scalds should add up to 100, i.e. denominator of 1
      const flourPct = toFixedDecimal((1 / (1 + liquidPctSum)) * sm.mainPct)
      submixFlourRatios.push({name: sm.name, percent: flourPct, percentStr: `${flourPct}`, ro: true, id: sm.id})
      const liquidPct = toFixedDecimal((liquidPctSum / (1 + liquidPctSum)) * sm.mainPct)
      submixLiquidRatios.push({name: sm.name, percent: liquidPct, percentStr: `${liquidPct}`, ro: true, id: sm.id})
    })
    return {flourRatios: submixFlourRatios, liquidRatios: submixLiquidRatios}
  }

  let readOnlyFlour = []
  let readOnlyLiquid = []

  const prefermentFlourRatio = getPrefermentFlourRatio()
  if (prefermentFlourRatio !== undefined) {
    readOnlyFlour.push(prefermentFlourRatio)
  }
  const prefermentLiquidRatio = getPrefermentLiquidRatio()
  if (prefermentLiquidRatio !== undefined) {
    readOnlyLiquid.push(prefermentLiquidRatio)
  }
  // account for scald and porridge submix flours and liquids
  const scaldRatios = getScaldRatios()
  if (scaldRatios.flourRatios.length > 0) {
    readOnlyFlour = readOnlyFlour.concat(scaldRatios.flourRatios)
  }
  if (scaldRatios.liquidRatios.length > 0) {
    readOnlyLiquid = readOnlyLiquid.concat(scaldRatios.liquidRatios)
  }

  return (
          <Grid item container direction={'column'}>
            <div>
              {formData.includePreferment ? renderPrefermentRatio() : renderMainLeavening()}
              {extraLeavening ? renderExtraMainLeavening() : null}
              <FermentationTimingControl headingLabel={'Bulk Ferm'} fermPctValueArr={doughVolumes} fermTempValueArr={fermTemps}
                                         timeDisplayValue={formData.includePreferment ? prefermentMainDoughTimeFormatted : mainDoughTimeFormatted}
                                         timeDisplayWarning={formData.includePreferment ? prefermentMainDoughWarningMsg : () => undefined}
                                         fermPctValue={bulkTimingPct} fermPctName={'bulkTimingPct'} fermPctLabel={'Bulk Ferm Goal'}
                                         fermStateLabel={'rise'} fermTempValue={bulkTimingTempF} fermTempName={'bulkTimingTempF'} fermTempLabel={'Bulk Ferm Temp'}
                                         onChange={onBulkTimingParamChange} onHelpClick={() => openHelp(undefined, helpFermVolTemp)}/>
              <Divider textAlign={'right'} sx={{marginTop: 1, width: '90%'}}>
                <Typography sx={{fontSize: 12, fontWeight: 350}}>Flour</Typography>
              </Divider>
              <Grid item container direction={'column'} alignItems={'flex-start'} >
                <IngredientGroupForm ingredientData={formData.flour} setIngredientData={updateFlourData} balanceGoal={100} idField={'id'} nameField={'name'}
                           unitField={'percent'} groupName={'Flour'} minGroupSize={1} disabled={false} readOnlyIngredients={readOnlyFlour}
                           ingredientType={ingredientTypeGrain} updateIngredientList={updateIngredientList} componentId={componentId}/>
              </Grid>
            </div>
            <Divider textAlign={'right'} sx={{marginTop: 1.5, width: '90%'}}>
              <Typography sx={{fontSize: 12, fontWeight: 350}}>Liquid</Typography>
            </Divider>
            <Grid item container direction={'column'} alignItems={'flex-start'} >
              <IngredientGroupForm ingredientData={formData.liquid} setIngredientData={updateLiquidData} balanceGoal={formData.hydrationPct} idField={'id'}
                          nameField={'name'} unitField={'percent'} minGroupSize={1} groupName={'Liquid'} disabled={false} readOnlyIngredients={readOnlyLiquid}
                          ingredientType={ingredientTypeLiquid} updateIngredientList={updateIngredientList} componentId={componentId}/>
            </Grid>

            <Divider textAlign={'right'} sx={{marginTop: 1.5, width: '90%'}}>
              <Typography sx={{fontSize: 12, fontWeight: 350}}>Inclusions</Typography>
            </Divider>
            <Grid item container direction={'column'} alignItems={'flex-start'} >
              <IngredientCollectionForm ingredientData={formData.mixin} setIngredientData={updateMixinData} idField={'id'}
                                        nameField={'name'} unitField={'percent'} collectionName={'Inclusion'} ingredientType={ingredientTypeInclusion}
                                        updateIngredientList={updateIngredientList} componentId={componentId}/>
            </Grid>
            <Divider textAlign={'right'} sx={{marginTop: 1.5, width: '90%'}}>
              <Typography sx={{fontSize: 12, fontWeight: 350}}>Toppings</Typography>
            </Divider>
            <Grid item container direction={'column'} alignItems={'flex-start'} >
              <IngredientGroupForm ingredientData={formData.toppings} setIngredientData={updateToppingsData} balanceGoal={100} idField={'id'}
                                   nameField={'name'} unitField={'percent'} groupName={'Toppings'} minGroupSize={0} disabled={false}
                                   ingredientType={ingredientTypeTopping} updateIngredientList={updateIngredientList} componentId={componentId}/>
            </Grid>

            <ContextualHelpDialog isOpen={openContextualHelpDialog} close={onCloseHelp}
                                  title={contextualHelpContent.title} content={contextualHelpContent.content}/>
          </Grid>
  )
}

export { MainDoughForm }